import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMyEvents } from '../api/events';
import { useMyProgress } from '../api/takes';
import { useSongs } from '../api/songs';
import { useMe, useMembership } from '../auth/session';
import { ErrorView } from '../components/Guards';
import { AwaitingReply, NextEventCard, RequestedSubmissions, UpcomingEvents } from '../components/HomeEvents';
import { SongsTable } from '../components/SongsTable';
import ui from '../styles/ui.module.css';
import styles from './ChoristHomePage.module.css';

/**
 * Chorist home (wireframe 01): the next event the chorist takes part in and its songs, with the
 * director's grade, where the latest submission stands and the director's comments; requested
 * submissions; the other upcoming events; and an event waiting for their reply.
 */
export function ChoristHomePage() {
  const { t } = useTranslation();
  const me = useMe().data;
  const membership = useMembership();
  const songs = useSongs(membership?.choirId);
  const progress = useMyProgress(membership?.choirId);
  const myEvents = useMyEvents(membership?.choirId);

  if (!membership) {
    return (
      <div className={ui.page}>
        <p>{t('common.noChoir')}</p>
      </div>
    );
  }
  const part = membership.defaultPart;
  const firstName = me?.displayName.split(' ')[0] ?? '';
  const bySong = new Map((progress.data ?? []).map((p) => [p.songId, p]));
  const events = myEvents.data ?? [];
  // The next event the chorist takes part in, and an event in their line-up still waiting for a reply.
  const next = events.find((e) => e.participation === 'IN');
  const awaiting = events.find((e) => e.participation === 'IN' && !e.answer && e.id !== next?.id);
  const others = events.filter((e) => e.id !== next?.id);
  // The songs table follows the next event's repertoire when it has one; Kitaplık has them all.
  const eventSongIds = next && next.songs.length > 0 ? next.songs.map((x) => x.songId) : null;
  const allSongs = songs.data ?? [];
  const shownSongs =
    eventSongIds
      ? eventSongIds.map((id) => allSongs.find((x) => x.id === id)).filter((x): x is NonNullable<typeof x> => !!x)
      : allSongs;

  return (
    <div className={awaiting ? `${ui.page} ${styles.wide}` : ui.page}>
      <div className={ui.pageHeader}>
        <div>
          <div className={ui.eyebrow}>{membership.choirName}</div>
          <h1>{t('home.greeting', { name: firstName })}</h1>
          <div className={ui.subtitle}>
            {part && t('home.yourPart', { part: t(`parts.${part}`) })}
            {membership.leadsSection && (
              <>
                {' · '}
                <span className={`${ui.chip} ${ui.chipSoft}`} data-testid="section-leader">
                  {t('home.leads', { part: t(`parts.${membership.leadsSection}`) })}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={awaiting ? styles.withSide : undefined}>
      <div className={styles.main}>
      {next && <NextEventCard choirId={membership.choirId} event={next} />}
      <section className={ui.card} data-testid="songs-card" data-events={myEvents.isPending ? 'loading' : 'ready'}>
        <div className={styles.songsHeader}>
          <h2 className={ui.cardTitle}>{eventSongIds && next ? t('myEvents.eventSongs', { name: next.name }) : t('home.songs')}</h2>
          {eventSongIds && (
            <Link to="/library" data-testid="all-songs-link">
              {t('myEvents.showAll', { count: allSongs.length })}
            </Link>
          )}
        </div>
        {songs.isPending && <p className={ui.muted}>{t('common.loading')}</p>}
        {songs.isError && <ErrorView onRetry={() => void songs.refetch()} />}
        {songs.data && songs.data.length === 0 && <p className={ui.empty}>{t('home.noSongs')}</p>}
        {songs.data && songs.data.length > 0 && <SongsTable songs={shownSongs} part={part} progress={bySong} />}
      </section>
      <RequestedSubmissions events={events} />
      <UpcomingEvents events={others} />
      </div>
      {awaiting && (
        <aside className={styles.side}>
          <AwaitingReply choirId={membership.choirId} event={awaiting} />
        </aside>
      )}
      </div>
    </div>
  );
}
